import React from 'react'
import { Box, Button, Grid, Chip, InputLabel, Typography, makeStyles } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { useHomeStore } from 'context/HomeStore'
import StandardTable from 'asterX-UI/Table/StandardTable'
import { Pagination } from 'asterX-UI/Pagination'
import { useAuthStore } from 'context/AuthStore'
import { useRoutes } from 'lib/hooks'
import { APPLICATION_PATH } from 'routes/constants.routes'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { ymdToDate, dateToYMD, formatHumanDateRange, isFutureDate } from 'lib/utils'
import { MainTabs } from '../../MainTabs'
import { Filter } from '../../Filter'
import { applicationColumns, applicationRowsFromApplications } from './const'

const useStyles = makeStyles(theme => ({
  dialogContainer: {
    '& .MuiPaper-root': {
      maxWidth: props => props?.maxWidth || ''
    }
  },
  container: {
    display: 'flex',
    flexDirection: 'column'
  }
}))

const ALL_STATUSES = [
  { label: 'Согласовано', value: 'COMPLETED' },
  { label: 'Отклонено', value: 'REJECTED' },
  { label: 'На согласовании', value: 'SUBMITTED' }
]

const buildDatePresetsLocal = (now = new Date()) => {
  const toISODate = d => {
    const pad = n => (n < 10 ? '0' + n : '' + n)
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  }
  const addDays = (date, n) => {
    const d = new Date(date)
    d.setDate(d.getDate() + n)
    return d
  }
  const today = toISODate(now)
  const yesterday = toISODate(addDays(now, -1))
  const last7From = toISODate(addDays(now, -6))
  const last7To = today
  const curFrom = toISODate(new Date(now.getFullYear(), now.getMonth(), 1))
  const prevFrom = toISODate(new Date(now.getFullYear(), now.getMonth() - 1, 1))
  const prevTo = toISODate(new Date(now.getFullYear(), now.getMonth(), 0))
  return [
    { key: 'today', label: 'Сегодня', from: today, to: today },
    { key: 'yesterday', label: 'Вчера', from: yesterday, to: yesterday },
    { key: '7d', label: '7 дней', from: last7From, to: last7To },
    { key: 'curMonth', label: 'Текущий месяц', from: curFrom, to: today },
    { key: 'prevMonth', label: 'Прошлый месяц', from: prevFrom, to: prevTo },
    { key: 'custom', label: 'Свой', from: '', to: '' }
  ]
}

const ApplicationTab = () => {
  const { authStore, authStoreFunc } = useAuthStore()
  const { homeStore, homeStoreFunc } = useHomeStore()
  const routes = useRoutes()
  const classes = useStyles()

  const list = homeStore?.applicationList || []

  const presets = React.useMemo(() => buildDatePresetsLocal(), [])
  const [presetKey, setPresetKey] = React.useState('7d')
  const [from, setFrom] = React.useState(presets.find(p => p.key === '7d').from)
  const [to, setTo] = React.useState(presets.find(p => p.key === '7d').to)

  const [statuses, setStatuses] = React.useState(['COMPLETED'])
  const [allSelected, setAllSelected] = React.useState(false)
  const defaultSelection = ['COMPLETED']

  const [exportOpen, setExportOpen] = React.useState(false)

  const dFrom = ymdToDate(from)
  const dTo = ymdToDate(to)

  const datesFilled = !!from && !!to
  const onlyFutureOk = datesFilled && !isFutureDate(from) && !isFutureDate(to)

  const sortDatesAsc = (a, b) => {
    const da = ymdToDate(a)
    const db = ymdToDate(b)
    if (da && db && da > db) return [b, a]
    return [a, b]
  }
  const [visFrom, visTo] = sortDatesAsc(from, to)
  const humanRange = formatHumanDateRange(visFrom, visTo)

  const tableData = {
    columns: applicationColumns(),
    rows: applicationRowsFromApplications(list)
  }

  const handleOpen = row => {
    const btnCell = row.find(c => c?.isButton)
    const appId = btnCell?.data?.id
    if (!appId) return
    routes.goTo(`${APPLICATION_PATH}/${appId}?src=list`)
  }

  const applyPreset = key => {
    setPresetKey(key)
    const p = presets.find(x => x.key === key)
    if (!p) return
    if (key === 'custom') {
      setFrom('')
      setTo('')
      return
    }
    setFrom(p.from)
    setTo(p.to)
  }

  const openExport = () => setExportOpen(true)
  const closeExport = () => {
    setExportOpen(false)
    setStatuses(['COMPLETED'])
  }

  const confirmExport = () => {
    if (!onlyFutureOk) return
    const [fromDate, toDate] = sortDatesAsc(from, to)
    const payload = {
      fromDate,
      toDate,
      statuses: allSelected ? ALL_STATUSES.map(s => s.value) : statuses.length ? statuses : defaultSelection
    }
    homeStoreFunc?.handleExport && homeStoreFunc.handleExport(payload)
    setExportOpen(false)
    setStatuses(['COMPLETED'])
  }

  const toggleStatus = val => {
    setAllSelected(false)
    setStatuses(prev => {
      const has = prev.includes(val)
      const next = has ? prev.filter(v => v !== val) : [...prev, val]
      return next
    })
  }

  const handleAll = () => {
    setAllSelected(true)
    setStatuses([])
  }

  const renderMobile = () => (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginBottom: 10 }}>
      <StandardTable tableData={tableData} onRowClick={handleOpen} />
    </Box>
  )

  const rowTooltip = row => {
    const btnCell = row?.find(c => c && c.isButton)
    const data = btnCell?.data
    if (data?.status === 'REJECTED' && data?.rejectReason) {
      return {
        type: 'error',
        title: 'Причина отказа',
        message: data.rejectReason
      }
    }
    return null
  }

  return (
    <MainTabs>
      {authStore.isMobile ? (
        renderMobile()
      ) : (
        <>
          <Filter page={'application'}/>
          {authStoreFunc?.isAdmin() && (
            <Grid container spacing={2} style={{ marginBottom: 16, paddingRight:16 }}>
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  style={{ minWidth: 220, height: 48, fontWeight: 600, transition: 'all .2s' }}
                  onClick={openExport}
                  disabled={homeStore?.loading}
                >
                  Выгрузить в Excel
                </Button>
              </Grid>
            </Grid>
          )}

          <ActionModal
            open={exportOpen}
            onClose={closeExport}
            onCancel={closeExport}
            onConfirm={confirmExport}
            disableConfirm={!onlyFutureOk || homeStore?.loading}
            title="Выгрузка по диапазону и статусу"
            iconComponent={null}
            rootStyles={classes}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 16 }}>
                  {presets.map(p => (
                    <Chip
                      key={p.key}
                      label={p.label}
                      color={presetKey === p.key ? 'primary' : 'default'}
                      onClick={() => applyPreset(p.key)}
                      style={{ borderRadius: 24 }}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  variant="dialog"
                  inputVariant="outlined"
                  DialogProps={{
                    BackdropProps: { style: { backgroundColor: 'rgba(0,0,0,0.12)' } }
                  }}
                  autoOk
                  label="С"
                  format="dd.MM.yyyy"
                  value={dFrom}
                  onChange={date => setFrom(date ? dateToYMD(date) : '')}
                  fullWidth
                  disableFuture
                  disabled={presetKey !== 'custom'}
                  placeholder="Выберите дату"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  variant="dialog"
                  inputVariant="outlined"
                  DialogProps={{
                    BackdropProps: { style: { backgroundColor: 'rgba(0,0,0,0.12)' } }
                  }}
                  autoOk
                  label="По"
                  format="dd.MM.yyyy"
                  value={dTo}
                  onChange={date => setTo(date ? dateToYMD(date) : '')}
                  fullWidth
                  disableFuture
                  disabled={presetKey !== 'custom'}
                  placeholder="Выберите дату"
                />
              </Grid>

              <Grid item xs={12}>
                <InputLabel style={{marginBottom:10}} shrink>Статусы</InputLabel>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  <Chip
                    label="Все"
                    onClick={handleAll}
                    variant={allSelected ? 'default' : 'outlined'}
                    color={allSelected ? 'primary' : 'default'}
                    style={{ borderRadius: 20 }}
                  />
                  {ALL_STATUSES.map(s => {
                    const active = !allSelected && statuses.includes(s.value)
                    return (
                      <Chip
                        key={s.value}
                        label={s.label}
                        onClick={() => toggleStatus(s.value)}
                        variant={active ? 'default' : 'outlined'}
                        color={active ? 'primary' : 'default'}
                        style={{ borderRadius: 20 }}
                      />
                    )
                  })}
                </Box>
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                  Статус по умолчанию: <b>Согласовано</b>.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" style={{ color: !onlyFutureOk ? '#D92D20' : 'inherit' }}>
                  {humanRange}
                  {!onlyFutureOk && (from || to) ? ' • Будущие даты недоступны' : ''}
                </Typography>
              </Grid>
            </Grid>
          </ActionModal>

          <StandardTable
            tableData={tableData}
            onRowClick={handleOpen}
            btnStyle={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: 8,
              minWidth: 'auto',
              cursor: 'pointer'
            }}
            tableStyles={{
              '--table-layout': 'fixed',
              '--th-white-space': 'normal',
              '--th-word-break': 'break-word'
            }}
            rowTooltip={rowTooltip}
          />
        </>
      )}

      <Grid item xs={12}>
        <Pagination
          count={Math.max(
            1,
            Math.ceil((homeStore?.applicationPagination?.count || 0) / (homeStore?.applicationPagination?.limit || 30))
          )}
          page={(homeStore?.applicationPagination?.offset || 0) + 1}
          onChange={homeStoreFunc.handlePaginationChange}
          isMobile={authStore.isMobile}
          disabled={homeStore?.loading}
        />
      </Grid>
    </MainTabs>
  )
}

export default ApplicationTab
